import * as React from 'react';
import { Box, IconButton, List, ListItem, ListItemText, MenuItem, Popover, Typography } from '@mui/material';
import {
  FormatListBulleted,
  FormatListNumbered,
  FormatListNumberedRtl,
  FormatIndentDecrease,
  FormatIndentIncrease,
} from '@mui/icons-material';
import { LexicalEditor, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND } from 'lexical';

import {
  CHANGE_LIST_TYPE_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListType,
  REMOVE_LIST_COMMAND,
} from '../../nodes/PhxListNode';

import { listTypeToBlockName } from './index';
import { translate as t } from 'hooks/useTranslations';

const rootTypeToRootName = {
  root: 'Root',
  table: 'Table',
};

const availableListTypes = ['bullet', 'number', 'alpha', 'legacy_number', 'legacy_alpha'];

type ListFormatProps = {
  blockType: keyof typeof listTypeToBlockName;
  rootType: keyof typeof rootTypeToRootName;
  editor: LexicalEditor;
  disabled?: boolean;
};

export default function ListFormat({ editor, blockType, rootType, disabled = false }: ListFormatProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'list-format-popover' : undefined;

  const listStyles = {
    bullet: ['square', 'circle'],
    number: ['decimal', 'lower-alpha', 'lower-roman'],
    alpha: ['upper-alpha', 'lower-alpha', 'lower-roman'],
    legacy_number: ['decimal', 'lower-alpha'],
    legacy_alpha: ['lower-alpha', 'decimal'],
  };
  const DynamicList = ({ type }: { type: keyof typeof listStyles }) => {
    const styles = listStyles[type];
    const levels = styles.length;

    const renderList = (styles: string[], depth: number): JSX.Element => {
      if (depth === 0) return <></>;

      return (
        <List sx={{ listStyleType: styles[0], padding: '0px', paddingLeft: '16px' }}>
          <ListItem sx={{ display: 'list-item', paddingRight: '0px', paddingBottom: '0px' }}>
            <ListItemText primary={`Level ${levels - depth + 1}`} />
            {renderList(styles.slice(1), depth - 1)}
          </ListItem>
        </List>
      );
    };

    return <>{renderList(styles, levels)}</>;
  };

  return (
    <Box>
      <IconButton
        size='small'
        disabled={disabled}
        onClick={() => {
          if (blockType === 'bullet') {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          }
          if (availableListTypes.includes(blockType as ListType)) {
            editor.dispatchCommand(CHANGE_LIST_TYPE_COMMAND, 'bullet');
          } else {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }
          handleClose();
        }}
        title={t('contentEditor.toolbar.list.bullet.title')}
        aria-label={t('contentEditor.toolbar.list.bullet.label')}
      >
        <FormatListBulleted fontSize='small' />
      </IconButton>
      <IconButton
        size='small'
        disabled={disabled}
        onClick={handleClick}
        className='toolbar-item'
        title={t('contentEditor.toolbar.list.format.title')}
        aria-label={t('contentEditor.toolbar.list.format.label')}
      >
        <FormatListNumbered fontSize='small' />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: '12px !important',
          },
        }}
      >
        <Box
          display='grid'
          gridTemplateColumns='repeat(2, 1fr)'
        >
          <MenuItem
            onClick={() => {
              if (blockType === 'number') {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
              } else if (availableListTypes.includes(blockType as ListType)) {
                editor.dispatchCommand(CHANGE_LIST_TYPE_COMMAND, 'number');
              } else {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, 'number');
              }
              handleClose();
            }}
            title={t('contentEditor.toolbar.list.number.title')}
            aria-label={t('contentEditor.toolbar.list.number.label')}
            sx={{
              padding: '8px',
            }}
          >
            <Box
              display='flex'
              flexDirection='column'
              sx={{
                border: '1px solid #ccc',
                borderRadius: '12px',
                padding: '4px',
                width: '100%',
              }}
            >
              <Typography>{t('contentEditor.toolbar.list.number.title')}</Typography>
              <DynamicList type='number' />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (blockType === 'alpha') {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
              } else if (availableListTypes.includes(blockType as ListType)) {
                editor.dispatchCommand(CHANGE_LIST_TYPE_COMMAND, 'alpha');
              }
              {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, 'alpha');
              }
              handleClose();
            }}
            title={t('contentEditor.toolbar.list.alpha.title')}
            aria-label={t('contentEditor.toolbar.list.alpha.label')}
            sx={{
              padding: '8px',
            }}
          >
            <Box
              display='flex'
              flexDirection='column'
              sx={{
                border: '1px solid #ccc',
                borderRadius: '12px',
                padding: '4px',
                width: '100%',
              }}
            >
              <Typography>{t('contentEditor.toolbar.list.alpha.title')}</Typography>
              <DynamicList type='alpha' />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (blockType === 'legacy_number') {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
              } else if (availableListTypes.includes(blockType as ListType)) {
                editor.dispatchCommand(CHANGE_LIST_TYPE_COMMAND, 'legacy_number');
              }
              {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, 'legacy_number');
              }
              handleClose();
            }}
            title={t('contentEditor.toolbar.list.legacyNumber.title')}
            aria-label={t('contentEditor.toolbar.list.legacyNumber.label')}
            sx={{
              padding: '8px',
            }}
          >
            <Box
              display='flex'
              flexDirection='column'
              sx={{
                border: '1px solid #ccc',
                borderRadius: '12px',
                padding: '4px',
                width: '100%',
              }}
            >
              <Typography>{t('contentEditor.toolbar.list.legacyNumber.title')}</Typography>
              <DynamicList type='legacy_number' />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (blockType === 'legacy_alpha') {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
              } else if (availableListTypes.includes(blockType as ListType)) {
                editor.dispatchCommand(CHANGE_LIST_TYPE_COMMAND, 'legacy_alpha');
              }
              {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, 'legacy_alpha');
              }
              handleClose();
            }}
            title={t('contentEditor.toolbar.list.legacyAlpha.title')}
            aria-label={t('contentEditor.toolbar.list.legacyAlpha.label')}
            sx={{
              padding: '8px',
            }}
          >
            <Box
              display='flex'
              flexDirection='column'
              sx={{
                border: '1px solid #ccc',
                borderRadius: '12px',
                padding: '4px',
                width: '100%',
              }}
            >
              <Typography>{t('contentEditor.toolbar.list.legacyAlpha.title')}</Typography>
              <DynamicList type='legacy_alpha' />
            </Box>
          </MenuItem>
        </Box>
      </Popover>
      <IconButton
        size='small'
        disabled={disabled}
        onClick={() => {
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        }}
        className='toolbar-item'
        title={t('contentEditor.toolbar.list.outdent.title')}
        aria-label={t('contentEditor.toolbar.list.outdent.label')}
      >
        <FormatIndentDecrease fontSize='small' />
      </IconButton>
      <IconButton
        size='small'
        disabled={disabled}
        onClick={() => {
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        }}
        className='toolbar-item'
        title={t('contentEditor.toolbar.list.indent.title')}
        aria-label={t('contentEditor.toolbar.list.indent.label')}
      >
        <FormatIndentIncrease fontSize='small' />
      </IconButton>
    </Box>
  );
}
